'use client';

import { useEffect } from 'react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import ReactModal from 'react-modal';
import useScrollBlock from '@/hooks/useScrollBlock';
import { Props } from './types';
import useClassNames from './hooks/useClassNames';

const Modal = ({
  children,
  preventScroll,
  style,
  closeButton,
  className = '',
  size = 'lg',
  variant = 'default',
  centered = true,
  ...props
}: React.PropsWithChildren<Props>) => {
  const { blockScroll } = useScrollBlock();

  useEffect(() => {
    if (!preventScroll) return;
    blockScroll(true);
    // EWM-323: release on unmount-while-open, else the shared scroll-block
    // counter leaks and the body stays locked for every later panel.
    return () => blockScroll(false);
  }, [blockScroll, preventScroll]);

  const { modalClassNames } = useClassNames({ className, size, centered, variant });

  const modalStyle: Props['style'] = {
    overlay: {
      zIndex: 51,
      backgroundColor: 'rgba(5, 16, 54, 0.2)',
      ...style?.overlay,
    },
    content: {
      padding: 0,
      ...style?.content,
    },
  };

  return (
    <ReactModal
      {...props}
      ariaHideApp={false}
      preventScroll={false}
      style={modalStyle}
      className={modalClassNames}
      parentSelector={() => document.getElementById('react-modal-custom-portal') ?? document.body}
    >
      {closeButton && (
        <CloseIcon
          data-testid="close-modal"
          className="absolute top-4 right-4 size-6 cursor-pointer text-gray-700"
          onClick={props.onRequestClose}
        />
      )}
      {children}
    </ReactModal>
  );
};

export default Modal;
